import { ReactElement, useCallback, useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Form, Formik } from 'formik';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import { UserContext } from 'context/User/UserContext';
import Yup from 'utils/validation/yup-extensions';
import { submitCommunityConnectUserCreation } from '../../_api';
import { NewCommunityConnectUserPayload } from '../../_classes';
import {
	DUPLICATE_REASONS,
	NEW_NON_EMPLOYEE_KEYS,
	PAGE_ROUTES,
} from '../../_const';
import {
	COMMUNITY_CONNECT_CONFIG,
	COMMUNITY_CONNECT_INITIAL_VALIDATION_SCHEMA,
	COMMUNITY_CONNECT_INITIAL_VALUES,
	COMMUNITY_CONNECT_USER,
} from './_config';
import {
	hasErrors,
	isStepError,
	navigate,
} from 'pages/AnnualUserAccessReview/_util';
import FixedBottomNavigation from 'pages/AnnualUserAccessReview/components/FixedBottomNavigation';
import CommunityConnectUserForm from './CommunityConnectUserForm';
import PrerequisiteCheck from '../../components/views/PrerequisiteCheck';
import DuplicateIdentity from '../../components/fields/DuplicateIdentity';
import ReviewAndSubmitCommunityConnectUser from './ReviewAndSubmitCommunityConnectUser';
import { NAVIGATION_ACTIONS } from 'pages/AnnualUserAccessReview/_const';
import { setSession } from 'store/utility';
import InitializationStep from 'pages/NewNonEmployee/components/views/InitializationStep';
import CommunityConnectLocation from 'pages/NewNonEmployee/components/fields/CommunityConnectLocation';
import { getLegacyPath } from 'routes/_utils';
import { debounceAsync } from 'utils/misc/debounce';
import SponsorFieldsContainer from 'pages/NewNonEmployee/components/views/SponsorFieldsContainer';

/**
 * Generate a form to request a new non-employee of type Community Connect User
 * @returns {ReactElement} Community Connect User request form
 */
const CommunityConnectUserContainer = () => {
	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const loggedInUser =
		contextUser.userInfo.impersonationProfile ??
		contextUser.userInfo.userProfile;
	const hasSiteAdminRole = loggedInUser?.Roles?.some(r => r.ID === 1612);
	const [sSubmissionStatus, setSubmissionStatus] = useState(null);
	const history = useHistory();
	const [sActiveStep, setActiveStep] = useState(0);
	const [sStepProgress, setStepProgress] = useState(0);

	const COMMUNITY_CONNECT_STEPS = {
		INITIALIZATION: 'Getting Started',
		PREREQUISITE_CHECK: 'Prerequisite Check',
		USER_INFO: 'User Information',
		VALIDATION_CHECK: 'Identity Validation',
		ACTION_SUMMARY: 'Review & Submit',
	};

	const calculateStepIndex = key => {
		return Object.values(COMMUNITY_CONNECT_STEPS).indexOf(key);
	};

	const stepLabels = Object.values(COMMUNITY_CONNECT_STEPS);

	const onNavigate = (type, goToStep = 0) => {
		let newStep = navigate(
			type,
			sActiveStep,
			stepLabels.length,
			null,
			null,
			null,
			goToStep,
		);
		setStepProgress(Math.max(newStep, sStepProgress));
		setActiveStep(newStep);
	};

	const renderStepContent = step => {
		switch (step) {
			//ask initial questions to determine the who and the what
			case 0:
				return (
					<InitializationStep
						label='Community Connect User'
						additionalFields={[
							<CommunityConnectLocation />,
							<SponsorFieldsContainer
								label={'Community Connect User'}
							/>,
						]}
					/>
				);
			//Prerequisite check on permissions and data integrity
			case 1:
				return (
					<PrerequisiteCheck
						name={NEW_NON_EMPLOYEE_KEYS.PREREQUISITE_CHECK}
						onNavigate={onNavigate}
					/>
				);
			//primary form
			case 2:
				return <CommunityConnectUserForm />;
			//duplicate identity check
			case 3:
				return (
					<DuplicateIdentity
						name={NEW_NON_EMPLOYEE_KEYS.DUPLICATE_ID_CHECK}
						loggedInUser={loggedInUser}
						onNavigate={onNavigate}
						extras={{
							UserType: COMMUNITY_CONNECT_USER.name,
							UserTypePrimaryId: COMMUNITY_CONNECT_USER.id,
						}}
					/>
				);
			//review and submit
			case 4:
				return (
					<ReviewAndSubmitCommunityConnectUser
						submissionStatus={sSubmissionStatus}
					/>
				);

			default:
				return <>Default</>;
		}
	};
	const submit = debounceAsync(async (values, setSubmitting) => {
		await submitCommunityConnectUserCreation({
			...new NewCommunityConnectUserPayload(values).prepare(loggedInUser),
		})
			.then(async response => {
				setSubmitting(false);
				if (response.data.payload?.successful === false) {
					let displayMessage = 'Submitted Unsuccessfully';
					if (
						response?.data?.messages &&
						response.data.messages.length > 0 &&
						response.data.messages[0].Value
					) {
						displayMessage = response.data.messages[0].Value;
					}
					setSubmissionStatus({
						error: true,
						submitted: false,
						message: displayMessage,
					});
				} else {
					setSubmissionStatus({
						error: false,
						submitted: true,
						message: 'Submitted Successfully',
					});

					if (response.data.payload?.appSelectionFlag) {
						await setSession(
							'pTrackingNo',
							'P' + response.data.payload.esoTrackingNumber,
						);
						await setSession(
							'newTrackingNumMod',
							response.data.payload.esoTrackingNumber,
						);
						await setSession('Request_AppMode', '2');
						await setSession(
							'RequestorESOFIID',
							loggedInUser?.ID?.toString(),
						);
						await setSession('RequestorESOID', loggedInUser?.ESOID);
						await setSession(
							'RequesteeESOFIID',
							loggedInUser?.ID?.toString(),
						);
						await setSession('id', loggedInUser?.ID?.toString());
						history.push(
							getLegacyPath(
								`/App/Index?tn=${response.data.payload.esoTrackingNumber}`,
							),
						);
					} else {
						//successful submission = set session variable and redirect to App Forms
						await setSession(
							'requestOrigin',
							PAGE_ROUTES.COMMUNITY_CONNECT_USER,
						);
						history.push(
							`/RRS/Request_ModifyDetail_View.aspx?tn=${response.data.payload.esoTrackingNumber}`,
						);
					}
				}
			})
			.catch(() => {
				setSubmitting(false);
				setSubmissionStatus({
					error: true,
					submitted: false,
					message: 'Submission was unsuccessful',
				});
			});
		setSubmitting(true);
	});
	const debounceSubmit = useCallback(submit, [submit]);

	return (
		<Box sx={{ width: '100%', minHeight: 200 }}>
			<Formik
				initialValues={{
					...COMMUNITY_CONNECT_INITIAL_VALUES,
					[NEW_NON_EMPLOYEE_KEYS.REQUESTOR_IS_SITE_ADMIN]:
						hasSiteAdminRole,
				}}
				validationSchema={Yup.object().shape({
					...COMMUNITY_CONNECT_INITIAL_VALIDATION_SCHEMA,
				})}
				validateOnMount
				onSubmit={async (values, { setSubmitting }) =>
					debounceSubmit(values, setSubmitting)
				}
			>
				{formik => (
					<Form>
						<Stepper activeStep={sActiveStep} alternativeLabel>
							{stepLabels.map((label, index) => {
								const labelProps = {};
								if (
									isStepError(
										formik,
										index,
										COMMUNITY_CONNECT_STEPS,
										COMMUNITY_CONNECT_CONFIG,
									)
								) {
									labelProps.error = true;
								}
								return (
									<Step key={'step_' + index}>
										<StepLabel {...labelProps}>
											{label}
										</StepLabel>
									</Step>
								);
							})}
						</Stepper>
						<Stack
							direction={'column'}
							sx={{
								mt: '2rem',
								pl: { sm: 0, lg: 10 },
								pr: { sm: 0, lg: 10 },
							}}
						>
							<Box sx={{ mt: 3 }}>
								{renderStepContent(sActiveStep)}
							</Box>
						</Stack>
						<FixedBottomNavigation
							onChange={type => {
								if (
									sActiveStep ===
										calculateStepIndex(
											COMMUNITY_CONNECT_STEPS.ACTION_SUMMARY,
										) &&
									type === NAVIGATION_ACTIONS.PREVIOUS
								) {
									onNavigate(
										NAVIGATION_ACTIONS.GOTO,
										calculateStepIndex(
											COMMUNITY_CONNECT_STEPS.USER_INFO,
										),
									);
								} else if (
									sActiveStep ===
										calculateStepIndex(
											COMMUNITY_CONNECT_STEPS.USER_INFO,
										) &&
									type === NAVIGATION_ACTIONS.PREVIOUS
								) {
									onNavigate(
										NAVIGATION_ACTIONS.GOTO,
										calculateStepIndex(
											COMMUNITY_CONNECT_STEPS.INITIALIZATION,
										),
									);
								} else {
									onNavigate(type);
								}

								//if on one of the last steps and go back, reset the dup ID check
								if (
									sActiveStep >
										calculateStepIndex(
											COMMUNITY_CONNECT_STEPS.USER_INFO,
										) &&
									type === NAVIGATION_ACTIONS.PREVIOUS
								) {
									formik.setFieldValue(
										NEW_NON_EMPLOYEE_KEYS.DUPLICATE_ID_CHECK,
										DUPLICATE_REASONS.LOADING,
									);
								}
							}}
							currentStep={sActiveStep}
							stepCount={stepLabels.length}
							hasErrors={hasErrors(formik)}
							// open={open}
							enableHome={false}
							enableRestart={false}
							isSubmitting={formik.isSubmitting}
							isSubmitted={sSubmissionStatus?.submitted}
							keepNextDisabled={
								(sActiveStep ===
									calculateStepIndex(
										COMMUNITY_CONNECT_STEPS.INITIALIZATION,
									) &&
									Object.keys(formik.errors).some(key =>
										[
											NEW_NON_EMPLOYEE_KEYS.IS_SITE_ADMIN,
											NEW_NON_EMPLOYEE_KEYS.SELF_SPONSOR,
											NEW_NON_EMPLOYEE_KEYS.SPONSOR,
											NEW_NON_EMPLOYEE_KEYS.WAVE_ONE_LOCATION,
										].includes(key),
									)) ||
								(sActiveStep ===
									calculateStepIndex(
										COMMUNITY_CONNECT_STEPS.PREREQUISITE_CHECK,
									) &&
									formik.values[
										NEW_NON_EMPLOYEE_KEYS.PREREQUISITE_CHECK
									] !== true) ||
								(sActiveStep ===
									calculateStepIndex(
										COMMUNITY_CONNECT_STEPS.USER_INFO,
									) &&
									Object.keys(formik.errors).some(key =>
										[
											NEW_NON_EMPLOYEE_KEYS.FIRST_NAME,
											NEW_NON_EMPLOYEE_KEYS.MIDDLE_INITIAL,
											NEW_NON_EMPLOYEE_KEYS.LAST_NAME,
											NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_TYPE,
											NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_NUMBER,
											NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_SPECIFIER,
											NEW_NON_EMPLOYEE_KEYS.ALTERNATE_EMAIL,
											NEW_NON_EMPLOYEE_KEYS.DATE_OF_BIRTH,
											NEW_NON_EMPLOYEE_KEYS.PHONE_NUMBER,
											NEW_NON_EMPLOYEE_KEYS.PHONE_NUMBER_EXTENSION,
											NEW_NON_EMPLOYEE_KEYS.THIRD_PARTY_ORGANIZATION,
											NEW_NON_EMPLOYEE_KEYS.ACKNOWLEDGEMENT,
											NEW_NON_EMPLOYEE_KEYS.JOB_TITLE,
										].includes(key),
									)) ||
								(sActiveStep ===
									calculateStepIndex(
										COMMUNITY_CONNECT_STEPS.VALIDATION_CHECK,
									) &&
									Object.keys(formik.errors).some(key =>
										[
											NEW_NON_EMPLOYEE_KEYS.DUPLICATE_ID_CHECK,
											NEW_NON_EMPLOYEE_KEYS.NOT_A_DUPLICATE_ACKNOWLEDGEMENT,
											NEW_NON_EMPLOYEE_KEYS.NOT_A_DUPLICATE_REASON,
										].includes(key),
									))
							}
							keepBackDisabled={
								sActiveStep ===
									calculateStepIndex(
										COMMUNITY_CONNECT_STEPS.VALIDATION_CHECK,
									) &&
								formik.values[
									NEW_NON_EMPLOYEE_KEYS.DUPLICATE_ID_CHECK
								] === DUPLICATE_REASONS.NONE
							}
						/>
					</Form>
				)}
			</Formik>
		</Box>
	);
};

export default CommunityConnectUserContainer;
